import BookButton from "@/components/BookButton";
import { PROPERTY } from "@/lib/constants";
import { useSEO, breadcrumbLd } from "@/lib/seo";

export default function Accessibility() {
  useSEO({
    title: "ADA Accessibility — Wheelchair Accessible Rooms",
    description:
      "Westbridge Inn & Suites offers ADA-accessible rooms with roll-in showers, grab bars, lowered mirrors, accessible outlets and free TTY / strobe / visual alert equipment.",
    path: "/accessibility",
    jsonLd: breadcrumbLd([{ name: "Home", path: "/" }, { name: "Accessibility", path: "/accessibility" }]),
  });
  return (
    <div data-testid="accessibility-page">
      <section className="pt-36 pb-12 bg-canvas">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-8">
            <p className="font-mono-label text-ember mb-4">Accessibility statement</p>
            <h1 className="font-display text-6xl md:text-7xl text-ink leading-[0.98]">
              Hospitality<br /><span className="italic">means everyone.</span>
            </h1>
          </div>
          <div className="md:col-span-4">
            <p className="text-ink-soft leading-relaxed">
              Westbridge Inn &amp; Suites is committed to providing a welcoming, accessible stay for every
              guest. Our public spaces, guest rooms and digital experience are built to meet ADA and
              WCAG 2.1 AA standards.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-canvas py-16 grid">
        <div className="max-w-[1400px] w-full mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-12">
          <div>
            <p className="font-mono-label text-ember mb-4">Accessible guest room</p>
            <ul className="space-y-3">
              {[
                "Wheelchair accessible",
                "Service animals always welcome",
                "Accessible power outlets",
                "Closed-captioning television",
                "Hearing-impaired light alerts (free portable equipment)",
                "Communication kit available with TTY machine, strobe light & visual door knocker",
                "Telephone with visual call alert (free portable equipment)",
                "Wheelchair-accessible route within the room",
                "Roll-in shower with a securely fastened folding seat",
                "Room meets ADA compliance standards",
                "Bathroom door and other hardware easily operable with one hand",
                "Clear floor space throughout the bathroom",
                "Slip-resistant flooring",
              ].map((f) => (
                <li key={f} className="border-b border-rule-subtle pb-3 text-ink-soft">
                  <span className="text-ember mr-2" aria-hidden="true">→</span>{f}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="font-mono-label text-ember mb-4">Accessible bathroom</p>
            <ul className="space-y-3">
              {[
                "Grab bars in shower with a shower seat",
                "Low-level towel hooks and racks",
                "Lower mirrors",
                "Removable shower head",
                "Roll-in shower",
                "Securely fastened folding shower seat",
                "Toilet and shower equipped with grab bars",
                "ADA-compliant fixtures and clearances",
              ].map((f) => (
                <li key={f} className="border-b border-rule-subtle pb-3 text-ink-soft">
                  <span className="text-ember mr-2" aria-hidden="true">→</span>{f}
                </li>
              ))}
            </ul>

            <div className="mt-12 bg-sand p-8">
              <p className="font-mono-label text-ember mb-3">Request accommodations</p>
              <p className="text-ink-soft leading-relaxed">
                Communication kits, TTY machines, strobe lights and visual door knockers are provided
                free of charge. Please request these at the time of booking by calling our 24-hour front
                desk at <a href={`tel:${PROPERTY.phone.replace(/[^\d+]/g, "")}`} className="link-underline text-ink">{PROPERTY.phone}</a>.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-ink text-canvas py-20" data-testid="accessibility-cta">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <h2 className="font-display text-5xl md:text-6xl leading-[1.02]">
              Reserve an accessible room.
            </h2>
            <p className="mt-6 text-canvas/80 text-lg">Book direct so we can confirm specific equipment in advance.</p>
          </div>
          <div className="md:col-span-4 md:text-right">
            <BookButton label="Book Direct & Save" testid="accessibility-book-btn" />
          </div>
        </div>
      </section>
    </div>
  );
}
